/**
 * Supabase Client Configuration
 * 
 * This file configures the Supabase client for the Zentrian application.
 * The client is used across all feature modules to interact with the database,
 * authentication, and Edge Functions.
 * 
 * @module services/supabase/client
 */

import { createClient, type Session } from '@supabase/supabase-js';
import type { Database } from './types';
import { createSessionGate } from './sessionGatedFetch';

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

export { supabaseUrl as SUPABASE_URL, supabaseAnonKey as SUPABASE_ANON_KEY };

// Check if Supabase is properly configured
export const isSupabaseConfigured = 
  supabaseUrl !== 'http://localhost:8000' || 
  (supabaseAnonKey !== 'placeholder-key' && supabaseAnonKey !== 'your-anon-key-here');

if (!isSupabaseConfigured) {
  console.warn(
    '⚠️ Supabase is not configured. Please update .env.local with your Supabase credentials.\n' +
    'The app will run but data fetching will fail until configured.'
  );
}

/**
 * `/rest/v1` resources that SIGNED-OUT pages legitimately read with the anon
 * key ('table' or 'rpc/<fn>'). Everything else is held back by the session gate
 * until a session exists (see ./sessionGatedFetch.ts).
 *
 * Every new public resource needs BOTH a matching anon GRANT (+ RLS policy) in
 * a migration AND an entry here — otherwise the signed-out page gets a local
 * 401 AUTH_SESSION_MISSING instead of the database answer.
 */
export const PUBLIC_REST_RESOURCES: ReadonlySet<string> = new Set([
  'rpc/get_employee_invitation',  // /employee/register token lookup (EmployeeRegister.tsx, #3559)
  'service_provider_invitations', // /service-provider/register token lookup (ServiceProviderRegister.tsx)
  'homeowner_member_invitations', // anon GRANT parity; MemberPasswordSetup.tsx reads it via its own client
]);

// Late-bound so Capacitor's native HTTP patch is picked up
// (Supabase captures fetch at init time, before Capacitor patches window.fetch)
const lateBoundFetch = (...args: Parameters<typeof fetch>) => fetch(...args);

// Kill switch: VITE_SESSION_GATE=off restores the plain transport (one-line rollback).
const sessionGateEnabled = import.meta.env.VITE_SESSION_GATE !== 'off';

const sessionGate = createSessionGate({
  supabaseUrl,
  anonKey: supabaseAnonKey,
  publicRestResources: PUBLIC_REST_RESOURCES,
  waitMs: 5000,
  // Lazy: only invoked after `supabase` exists (trackAuth below, or at request time).
  onAuthStateChange: (callback) => {
    const { data } = supabase.auth.onAuthStateChange(callback);
    return () => data.subscription.unsubscribe();
  },
  baseFetch: lateBoundFetch,
  onGate: import.meta.env.DEV
    ? (resource, outcome) => {
        // Resource name only — never headers or tokens.
        if (outcome === 'waiting') console.warn(`[sessionGate] anon-key request held until a session exists: ${resource}`);
        else if (outcome === 'blocked') console.warn(`[sessionGate] no session — answered locally (401 AUTH_SESSION_MISSING): ${resource}`);
      }
    : undefined,
});

/**
 * Supabase client instance
 * 
 * This client is configured with TypeScript types from the database schema.
 * Use this client for all Supabase operations.
 * 
 * @example
 * ```typescript
 * import { supabase } from '@/services/supabase/client';
 * 
 * // Query devices
 * const { data, error } = await supabase
 *   .from('devices')
 *   .select('*');
 * ```
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    // Session-gated (no PostgREST call leaves with the anon key while signed
    // in / signing out) over the late-bound transport; see sessionGatedFetch.ts.
    fetch: sessionGateEnabled ? sessionGate.fetch : lateBoundFetch,
    headers: {
      'X-Project-Ref': 'realtime-dev',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
    headers: {
      'X-Project-Ref': 'realtime-dev',
    },
  },
});

// Registered once, right after the client exists: keeps the gate's
// signed-in / signed-out view in step with auth events.
if (sessionGateEnabled) sessionGate.trackAuth();

/**
 * Mark the session as ended. Call BEFORE signOut: from then on anon-keyed
 * PostgREST requests are answered locally (401 AUTH_SESSION_MISSING) and never
 * reach the server. Cleared automatically by the next signed-in auth event.
 */
export const markSignedOut: () => void = sessionGate.markSignedOut;

/**
 * Get the current authenticated user
 * 
 * @returns The current user or null if not authenticated
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
  return user;
}

/**
 * Get the current session
 * 
 * @returns The current session or null if not authenticated
 */
export async function getCurrentSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error fetching current session:', error);
    return null;
  }
  return session;
}

/**
 * Ensure the restored auth session is attached before running an
 * authenticated query.
 *
 * On a cold page load the Supabase client restores the persisted session
 * asynchronously. Queries that fire before that finishes run as ANON, and RLS
 * returns 0 rows with NO error — which then gets cached/persisted, producing
 * the intermittent "No properties available" / missing owner controls (#2907).
 *
 * This waits for the session: returns it immediately if present, otherwise
 * listens for the next auth state change (session restore / sign-in) up to
 * `timeoutMs` before giving up. Returns null only when genuinely signed out.
 */
export async function ensureSession(timeoutMs = 4000): Promise<Session | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session) return session;

  return new Promise<Session | null>((resolve) => {
    let settled = false;
    const finish = (s: Session | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      subscription?.unsubscribe();
      resolve(s);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (nextSession) finish(nextSession);
    });
    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Sign out the current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Error signing out:', error);
    throw error;
  }
}
